import type { BuilderConfig, SelectedOptions } from './builder-types';
import type { FeatureDefinition } from './feature-modules/types';

export interface SpecFeatureWithDefinition {
  feature: {
    slug: string;
    name: string;
  };
  definition: FeatureDefinition;
  isRequired: boolean;
  defaultPreset: 'lightweight' | 'standard' | 'enterprise' | null | undefined;
}

function buildDefaultOptions(
  definition: FeatureDefinition,
  preset: 'lightweight' | 'standard' | 'enterprise',
): SelectedOptions {
  const options: SelectedOptions = {};
  for (const group of definition.optionGroups) {
    const matching = group.options.filter((o) => o.presets.includes(preset));
    if (matching.length === 0) continue;
    if (group.type === 'single') {
      options[group.id] = matching[0].id;
    } else {
      options[group.id] = matching.map((o) => o.id);
    }
  }
  return options;
}

export function applyPreset(
  specFeatures: SpecFeatureWithDefinition[],
  preset: 'lightweight' | 'standard' | 'enterprise',
): BuilderConfig {
  return {
    preset,
    features: Object.fromEntries(
      specFeatures.map((sf) => {
        const enabled =
          sf.isRequired ||
          (sf.defaultPreset != null
            ? sf.defaultPreset === preset || preset === 'enterprise'
            : true);
        return [
          sf.feature.slug,
          {
            enabled,
            options: buildDefaultOptions(sf.definition, preset),
          },
        ];
      }),
    ),
  };
}

export function getTransitiveDependencies(
  slug: string,
  allFeatures: SpecFeatureWithDefinition[],
): string[] {
  const deps = new Set<string>();
  const queue = [slug];
  while (queue.length > 0) {
    const current = queue.pop()!;
    const feature = allFeatures.find((f) => f.feature.slug === current);
    if (!feature) continue;
    for (const dep of feature.definition.dependencies) {
      if (!deps.has(dep)) {
        deps.add(dep);
        queue.push(dep);
      }
    }
  }
  return Array.from(deps);
}

export function getDependents(
  slug: string,
  allFeatures: SpecFeatureWithDefinition[],
  enabledSlugs: string[],
): string[] {
  return enabledSlugs.filter((s) => {
    if (s === slug) return false;
    const feature = allFeatures.find((f) => f.feature.slug === s);
    return feature?.definition.dependencies.includes(slug) ?? false;
  });
}
